import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';

import config from '../config';
import { Maybe } from '../gql/generated/graphql';

export type FinixAuth = {
  getSessionKey: () => string;
};

export const FINIX_SCRIPT = 'https://js.finix.com/v/1/finix.js';

export const FINIX_ENVIRONMENT = config.finix.environment;
export const FINIX_MERCHANT_ID = config.finix.merchantId;
export const FINIX_APPLICATION_ID = config.finix.applicationId;

export const createFinixAuth = async () =>
  new Promise<FinixAuth>((resolve, reject) => {
    const timeout = 10000; // 10 seconds
    const startTime = Date.now();
    const interval = setInterval(() => {
      if (Date.now() - startTime > timeout) {
        clearInterval(interval);
        reject(new Error('Finix script failed to load within timeout'));
        return;
      }
      if (window.Finix?.Auth) {
        clearInterval(interval);
        try {
          const finixAuth = window.Finix.Auth(FINIX_ENVIRONMENT, FINIX_MERCHANT_ID);
          resolve(finixAuth);
        } catch (error) {
          reject(error instanceof Error ? error : new Error('Failed to initialize Finix Auth'));
        }
      }
    }, 100);
  });

export interface PlaidData extends PlaidLinkOnSuccessMetadata {
  publicToken: string;
}

export const DOWN_PAYMENTS_STEPS = {
  BANK_ADDED: 'bankAdded',
  CARD_DETAILS: 'cardDetails',
  FAIL: 'fail',
  PAYMENT_INFO: 'paymentInfo',
  SUCCESS: 'success',
};

export interface PaymentData {
  id: Maybe<string>;
  amount?: Maybe<number>;
  cardBrand?: Maybe<string>;
  cardType?: Maybe<string>;
  failureCode?: Maybe<string>;
  maskedNumber?: Maybe<string>;
}

export const getFinixCardTokenForm = (formId: string, options: unknown) =>
  window.Finix?.CardTokenForm(formId, options);

export const getFinixTokenForm = (formId: string, options: unknown) =>
  window.Finix?.TokenForm(formId, options);

export const paymentFormOptions = {
  // https://finix.com/docs/guides/payments/online-payments/payment-details/token-forms/#step-3-define-input-fields
  // Supported Fields unless otherwise specified: "name", "number", "expiration_date", "security_code", "address_line1",
  // "address_line2", "address_city", "address_state", "address_region", "address_country", "address_postal_code"
  showAddress: true,
  showLabels: true,
  labels: {
    // custom labels
    security_code: 'CVV',
    address_line1: 'Address',
    address_line2: ' ', // Must be a space or label still shows
  },
  showPlaceholders: true,
  placeholders: {
    // custom placeholders
    number: 'Card Number',
    expiration_date: 'MM/YYYY',
    security_code: 'CVV',
    address_line1: 'Street',
    address_line2: 'Apt., Suite, Unit',
  },
  requiredFields: [
    // Supported Fields : "name", "address_line1", "address_line2", "address_city", "address_state", "address_region", "address_country", "address_postal_code"
    'name',
    'address_line1',
    'address_city',
    'address_state',
    'address_region',
    'address_country',
    'address_postal_code',
  ],
  hideErrorMessages: false, // if you want to require a field, but not hide input error messages (default is false)
  errorMessages: {
    name: 'Invalid name',
    address_city: 'Invalid city',
  },
  styles: {
    default: {
      background: '#efefef',
      color: '#444444',
      border: '0',
      borderRadius: '4px',
      padding: '0 10px',
      height: '36px',
      fontFamily: 'Helvetica',
      fontSize: '15px',
      boxShadow: 'none',
    },
    error: {
      color: '#BC5353', // specific styling if the field has errors
      background: 'rgba(255,0,0,0.15)',
    },
  },
};
